/**
 * Mirror of apps/api/src/app/schemas/analysis/{common,beams}.py
 *
 * Strict 1:1 with the backend so the form payload and the response can be
 * serialized/deserialized without runtime mapping.
 */

export type LiveLoadUse =
  | 'vivienda'
  | 'corredores_vivienda'
  | 'oficinas'
  | 'corredores_oficina'
  | 'aulas'
  | 'talleres_escolares'
  | 'hospitales_salas'
  | 'hospitales_corredores'
  | 'tiendas'
  | 'almacenes_livianos'
  | 'almacenes_pesados'
  | 'garajes_livianos'
  | 'garajes_pesados'
  | 'asambleas_fijas'
  | 'asambleas_moviles'
  | 'azotea_no_accesible'
  | 'azotea_accesible'
  | 'industrial';

export type SlabType =
  | 'ninguna'
  | 'aligerada_17'
  | 'aligerada_20'
  | 'aligerada_25'
  | 'aligerada_30'
  | 'maciza_10'
  | 'maciza_15'
  | 'maciza_20'
  | 'maciza_25';

export type WallType =
  | 'kk_15'
  | 'kk_25'
  | 'pandereta_12'
  | 'bloque_concreto'
  | 'drywall_doble';

export type SupportType =
  | 'simply_supported'
  | 'continuous_end'
  | 'continuous_interior'
  | 'fixed_fixed'
  | 'cantilever';

export type ZoneCode = 'Z1' | 'Z2' | 'Z3' | 'Z4';
export type UsageCategory = 'A1' | 'A2' | 'B' | 'C';
export type SoilProfile = 'S0' | 'S1' | 'S2' | 'S3';
export type SystemKind =
  | 'porticos_concreto'
  | 'dual_concreto'
  | 'muros_concreto'
  | 'mdl_concreto'
  | 'porticos_acero'
  | 'albañileria_armada'
  | 'albañileria_confinada'
  | 'madera';

export type CombinationKind = 'E060' | 'ACI318';

// ---------- Request ----------

export interface WallOnBeam {
  wall_type: WallType;
  height_m: number;
}

export interface BeamAnalysisGeometry {
  b: number;            // cm
  h: number;            // cm
  L_span_m: number;     // m
}

export interface BeamAnalysisAutoLoads {
  use: LiveLoadUse;
  slab_type: SlabType;
  tributary_width_m: number;
  acabados_kgf_m2: number;
  tabiqueria_kgf_m2: number;
  cielo_raso_kgf_m2: number;
  wall_on_beam?: WallOnBeam | null;
  custom_live_load_kgf_m2?: number | null;
}

export interface DistributedLoadExtra {
  label?: string;
  kind: 'dead' | 'live';
  magnitude: number;
  x_start_cm: number;
  x_end_cm: number;
}

export interface PointLoad {
  label?: string;
  kind: 'dead' | 'live';
  magnitude_kgf: number;
  x_cm: number;
}

export interface SeismicInput {
  zone: ZoneCode;
  usage: UsageCategory;
  soil: SoilProfile;
  system: SystemKind;
  building_height_m: number;
  seismic_weight_kgf: number;
  floor_shear_fraction: number;
  beam_shear_fraction: number;
}

export interface BeamAnalysisRequest {
  geometry: BeamAnalysisGeometry;
  support_type: SupportType;
  auto_loads: BeamAnalysisAutoLoads;
  extra_distributed: DistributedLoadExtra[];
  point_loads: PointLoad[];
  seismic?: SeismicInput | null;
  combo: CombinationKind;
  fc: number;
  fy: number;
  cover_cm: number;
  TMA_cm: number;
}

// ---------- Response ----------

export interface LoadBreakdown {
  q_slab_self: number;
  q_finishes: number;
  q_partitions: number;
  q_ceiling: number;
  q_D_surface: number;
  q_L_surface: number;
  beam_self_weight: number;
  wall_on_beam_load: number;
  w_D_auto: number;
  w_L_auto: number;
  w_D_extra: number;
  w_L_extra: number;
  w_D_total: number;
  w_L_total: number;
  w_u_linear: number;
}

export interface InternalForces {
  Mu_neg_left: number;     // kgf·cm
  Mu_pos_mid: number;
  Mu_neg_right: number;
  Vu_left: number;         // kgf
  Vu_right: number;
}

export interface DiagramSample {
  x_m: number;
  Mu_kgf_m: number;
  V_kgf: number;
}

export interface SeismicSummary {
  Z: number;
  U: number;
  S: number;
  R: number;
  C: number;
  Tp: number;
  Tl: number;
  T_building: number;
  V_basal: number;
  V_element: number;
  M_element: number;
}

export interface DesignPrefillLoads {
  Mu_neg_left: number;
  Mu_pos_mid: number;
  Mu_neg_right: number;
  Vu_left: number;
  Vu_right: number;
}

export interface BeamAnalysisResponse {
  breakdown: LoadBreakdown;
  forces: InternalForces;
  diagram: DiagramSample[];
  seismic: SeismicSummary | null;
  governing_combo: string;
  warnings: string[];
  design_prefill: DesignPrefillLoads;
  coefficients_used: Record<string, unknown>;
}

// ---------- UI labels (mirror of backend) ----------

export const USE_LABELS: Record<LiveLoadUse, string> = {
  vivienda: 'Vivienda',
  corredores_vivienda: 'Corredores de vivienda',
  oficinas: 'Oficinas',
  corredores_oficina: 'Corredores de oficina',
  aulas: 'Aulas',
  talleres_escolares: 'Talleres escolares',
  hospitales_salas: 'Hospitales — salas',
  hospitales_corredores: 'Hospitales — corredores',
  tiendas: 'Tiendas',
  almacenes_livianos: 'Almacenes livianos',
  almacenes_pesados: 'Almacenes pesados',
  garajes_livianos: 'Garajes livianos (autos)',
  garajes_pesados: 'Garajes pesados (camiones)',
  asambleas_fijas: 'Asambleas — asientos fijos',
  asambleas_moviles: 'Asambleas — asientos móviles',
  azotea_no_accesible: 'Azotea no accesible',
  azotea_accesible: 'Azotea accesible / terraza',
  industrial: 'Uso industrial',
};

export const USE_VALUES: Record<LiveLoadUse, number> = {
  vivienda: 200,
  corredores_vivienda: 200,
  oficinas: 250,
  corredores_oficina: 400,
  aulas: 250,
  talleres_escolares: 350,
  hospitales_salas: 300,
  hospitales_corredores: 400,
  tiendas: 500,
  almacenes_livianos: 500,
  almacenes_pesados: 1000,
  garajes_livianos: 250,
  garajes_pesados: 750,
  asambleas_fijas: 300,
  asambleas_moviles: 500,
  azotea_no_accesible: 100,
  azotea_accesible: 100,
  industrial: 750,
};

export const SLAB_LABELS: Record<SlabType, string> = {
  ninguna: 'Sin losa (viga aislada)',
  aligerada_17: 'Aligerada h=17 cm',
  aligerada_20: 'Aligerada h=20 cm',
  aligerada_25: 'Aligerada h=25 cm',
  aligerada_30: 'Aligerada h=30 cm',
  maciza_10: 'Maciza h=10 cm',
  maciza_15: 'Maciza h=15 cm',
  maciza_20: 'Maciza h=20 cm',
  maciza_25: 'Maciza h=25 cm',
};

export const SLAB_VALUES: Record<SlabType, number> = {
  ninguna: 0,
  aligerada_17: 280,
  aligerada_20: 300,
  aligerada_25: 350,
  aligerada_30: 420,
  maciza_10: 240,
  maciza_15: 360,
  maciza_20: 480,
  maciza_25: 600,
};

export const WALL_LABELS: Record<WallType, string> = {
  kk_15: 'Ladrillo King-Kong 15 cm',
  kk_25: 'Ladrillo King-Kong 25 cm',
  pandereta_12: 'Ladrillo Pandereta 12 cm',
  bloque_concreto: 'Bloque de concreto 15 cm',
  drywall_doble: 'Drywall doble cara',
};

export const SUPPORT_LABELS: Record<SupportType, string> = {
  simply_supported: 'Simplemente apoyada',
  continuous_end: 'Continua — vano de extremo',
  continuous_interior: 'Continua — vano interior',
  fixed_fixed: 'Empotrada-empotrada',
  cantilever: 'Voladizo',
};

export const SUPPORT_HINT: Record<SupportType, string> = {
  simply_supported: 'Mu(+) = wL²/8 · Vu = wL/2',
  continuous_end: 'Mu(-) = wL²/10, Mu(+) = wL²/14',
  continuous_interior: 'Mu(-) = wL²/11, Mu(+) = wL²/16',
  fixed_fixed: 'Mu(-) = wL²/12, Mu(+) = wL²/24',
  cantilever: 'Mu(-) = wL²/2 en empotramiento',
};

export const ZONE_LABELS: Record<ZoneCode, string> = {
  Z1: 'Z1 — Selva oriental (0.10g)',
  Z2: 'Z2 — Sierra interior (0.25g)',
  Z3: 'Z3 — Sierra/Selva (0.35g)',
  Z4: 'Z4 — Costa (0.45g)',
};

export const USAGE_LABELS: Record<UsageCategory, string> = {
  A1: 'A1 — Esencial (hospital, etc.)',
  A2: 'A2 — Importante',
  B: 'B — Reuniones / comercio',
  C: 'C — Común (vivienda / oficina)',
};

export const SOIL_LABELS: Record<SoilProfile, string> = {
  S0: 'S0 — Roca dura',
  S1: 'S1 — Suelo muy rígido',
  S2: 'S2 — Suelo intermedio',
  S3: 'S3 — Suelo blando',
};

export const SYSTEM_LABELS: Record<SystemKind, string> = {
  porticos_concreto: 'Pórticos de concreto armado',
  dual_concreto: 'Dual (pórticos + muros)',
  muros_concreto: 'Muros estructurales de concreto',
  mdl_concreto: 'Muros de ductilidad limitada',
  porticos_acero: 'Pórticos de acero',
  albañileria_armada: 'Albañilería armada',
  albañileria_confinada: 'Albañilería confinada',
  madera: 'Madera',
};
